import { createContext, ReactNode, useEffect, useMemo, useRef, useState, useContext } from 'react'
import dayjs from 'dayjs'
import { getAllDevices, getAllNumberOfStations } from '../services/equipment.service'
import { IDevice, INumberOfStations } from '../types/equipment.type'
import { IOperatingUnit } from '../../shared/types/api/user.type'
import { ISpecManRequestFrequency } from '../../shared/types/api/spectrum-manager.type'
import { getAllOperatingUnits } from '../services/request-form.service'
import {
  IPlatform,
  IFrequencyPlan,
  ISpectrumManagerContext,
  ILocationTemplateWithRegions,
} from '../types/spectrum-manager.type'
import {
  getAllPlatforms,
  getAllDetailedRequests,
  getAllLocationTemplatesWithRegions,
  getAllFrequencyPlans,
} from '../services/spectrum-manager.service'
import { AuthContext } from './auth.context'
import { parseRequests } from '../utils/functions'

export const SpectrumManagerContext = createContext<ISpectrumManagerContext>({
  devices: [],
  platforms: [],
  setDevices: () => {},
  setPlatforms: () => {},
  filterOptions: {},
  operatingUnits: [],
  numberOfStations: [],
  setFrequencyPlans: () => {},
  setFilterOptions: () => {},
  setOperatingUnits: () => {},
  detailedRequests: [],
  frequencyPlans: [],
  locationTemplatesWithRegions: [],
  setDetailedRequests: () => {},
  getDetailedRequests: () => {},
  setLocationTemplatesWithRegions: () => {},
})

export const SpectrumManagerProvider = ({ children }: { children: ReactNode }) => {
  const [devices, setDevices] = useState<IDevice[]>([])
  const [platforms, setPlatforms] = useState<IPlatform[]>([])
  const [operatingUnits, setOperatingUnits] = useState<IOperatingUnit[]>([])
  const [filterOptions, setFilterOptions] = useState<{
    locationId?: number
    status?: string
    exerciseId?: number
    search?: string
  }>({ status: 'NOT_STARTED' })
  const [numberOfStations, setNumberOfStations] = useState<INumberOfStations[]>([])
  const [detailedRequests, setDetailedRequests] = useState<ISpecManRequestFrequency[]>([])
  const [frequencyPlans, setFrequencyPlans] = useState<IFrequencyPlan[]>([])
  const [locationTemplatesWithRegions, setLocationTemplatesWithRegions] = useState<ILocationTemplateWithRegions[]>([])

  const abortControllerRef = useRef<AbortController | null>(null)

  const memoizedFilterOptions = useMemo(() => filterOptions, [filterOptions])

  const { user } = useContext(AuthContext)

  const getDevices = async () => {
    const data = await getAllDevices()

    if (data.success && data.data) {
      const parsedDevices = data.data.map((device) => ({
        ...device,
        last_updated: dayjs(device.last_updated).utc(true),
      }))

      setDevices(parsedDevices)
    }
  }

  const getNumberOfStations = async () => {
    const data = await getAllNumberOfStations()

    if (data.success && data.data) {
      setNumberOfStations(data.data)
    }
  }

  const getOperatingUnits = async () => {
    const data = await getAllOperatingUnits()

    if (data.success && data.data) {
      setOperatingUnits(data.data)
    }
  }

  const getLocationTemplatesWithRegions = async () => {
    const data = await getAllLocationTemplatesWithRegions()

    if (data.success && data.data) {
      setLocationTemplatesWithRegions(data.data)
    }
  }

  const getPlatforms = async () => {
    const data = await getAllPlatforms()

    if (data.success && data.data) {
      setPlatforms(data.data)
    }
  }

  const getDetailedRequests = async (userId: string | null) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    const controller = new AbortController()
    abortControllerRef.current = controller

    const data = await getAllDetailedRequests(filterOptions, controller.signal)

    if (data.success && data.data) {
      const parsedRequests = parseRequests(data.data)

      setDetailedRequests(parsedRequests)
    }
  }

  const getFrequencyPlans = async () => {
    const data = await getAllFrequencyPlans(filterOptions)

    if (data.data) {
      const parsedPlans = data.data.map((request) => ({
        ...request,
        start_date: dayjs(request.start_date).utc(true),
        end_date: dayjs(request.end_date).utc(true),
      }))

      setFrequencyPlans(parsedPlans)
    }
  }

  useEffect(() => {
    if (!user) return

    getDevices()
    getPlatforms()
    getOperatingUnits()
    getNumberOfStations()
    getLocationTemplatesWithRegions()
  }, [user])

  useEffect(() => {
    if (!user) return

    getFrequencyPlans()
  }, [user, memoizedFilterOptions])

  return (
    <SpectrumManagerContext.Provider
      value={{
        devices,
        setDevices,
        setPlatforms,
        platforms,
        filterOptions,
        operatingUnits,
        frequencyPlans,
        numberOfStations,
        setFilterOptions,
        setFrequencyPlans,
        setOperatingUnits,
        detailedRequests,
        getDetailedRequests,
        setDetailedRequests,
        locationTemplatesWithRegions,
        setLocationTemplatesWithRegions,
      }}
    >
      {children}
    </SpectrumManagerContext.Provider>
  )
}
